import { Box, Stack, Button, SvgIcon, useTheme, Divider } from '@mui/material';
import Typography from '@/components/Typography';
import useResponsive from '@/hooks/useResponsive';
import CryptoInput from '@/components/Calculator/CryptoInput';
import { useRouter } from 'next/router';
import getLocalFile from '@/utils/getLocalFile';
import { useEffect, useMemo, useState } from 'react';
import numberUtils from '@/utils/numberUtils';

type CalculatorProps = {
  title: string;
  toTmnPrice: number | string;
  toUSDTPrice: number | string;
};

const Calculator = ({ title, toTmnPrice, toUSDTPrice }: CalculatorProps) => {
  const theme = useTheme();
  const { matches } = useResponsive();
  let {
    query: { symbol }
  } = useRouter();

  const marketName = symbol?.toString().toUpperCase() || '';
  const data = getLocalFile(marketName, 'info');
  const cryptoOptionsReceiveInput = [
    { name: 'TMN', logo: '/assets/coins/Toman.svg' },
    { name: 'USDT', logo: '/assets/coins/Tether.svg' }
  ];

  const logoAddress = useMemo(() => {
    const address = 'https://p.rabex.ir/api/v1/wallet/static/img';
    return `${address}/${data?.Information?.symbol}.svg`;
  }, [data]);

  const cryptoOptionsPayInput = [
    {
      name: data.Information.symbol,
      logo: logoAddress
    }
  ];

  // Define state for both inputs
  const [payValue, setPayValue] = useState<number | string>(1);
  const [receiveValue, setReceiveValue] = useState<number | string>('');
  const [payCrypto, setPayCrypto] = useState(cryptoOptionsPayInput[0].name);
  const [receiveCrypto, setReceiveCrypto] = useState(
    cryptoOptionsReceiveInput[0].name
  );

  const tmnToCryptoRate = numberUtils.removeDelimiter(String(toTmnPrice));
  const usdtToCryptoRate = numberUtils.removeDelimiter(String(toUSDTPrice));

  useEffect(() => {
    if (payValue) {
      handlePayValueChange(numberUtils.removeDelimiter(String(payValue)));
    }
  }, [receiveCrypto]);

  const handlePayValueChange = (value: string | number) => {
    setPayValue(numberUtils.delimiter(value));
    if (receiveCrypto === 'TMN') {
      setReceiveValue(numberUtils.delimiter(Number(value) * tmnToCryptoRate));
    } else if (receiveCrypto === 'USDT') {
      setReceiveValue(numberUtils.delimiter(Number(value) * usdtToCryptoRate));
    }
  };

  // Handle value changes for the "receive" input
  const handleReceiveValueChange = (value: string | number) => {
    setReceiveValue(numberUtils.delimiter(value));
    if (receiveCrypto === 'TMN') {
      setPayValue(
        numberUtils.delimiter(
          numberUtils.removeDelimiter(String(value)) / tmnToCryptoRate
        )
      ); // Multiply by USDT factor
    } else if (receiveCrypto === 'USDT') {
      setPayValue(
        numberUtils.delimiter(
          numberUtils.removeDelimiter(String(value)) / usdtToCryptoRate
        )
      ); // No conversion needed for USDT
    }
  };

  return (
    <Stack
      width={{ xs: '80vw', lg: 389 }}
      height={{ xs: 410, lg: 439 }}
      borderRadius={{ xs: 3, lg: 3 }}
      mt={{ xs: 10 }}
      sx={{
        backgroundColor: '#FFFFFF',
        position: 'relative',
        boxShadow: '0px 0px 1px 0px #00000066'
      }}
    >
      <Stack alignItems="center">
        <Typography
          variant={matches({ xs: 'body1', lg: 'subtitle' })}
          weight="bold"
          sx={{ marginY: matches({ xs: 5.5, lg: 7 }) }}
          color="#333333"
          component="h2"
        >
          {title}
        </Typography>
        {/*<Divider*/}
        {/*  orientation="horizontal"*/}
        {/*  variant="middle"*/}
        {/*  sx={{*/}
        {/*    width: matches({ xs: '100%', lg: 337 }),*/}
        {/*    borderStyle: 'solid',*/}
        {/*    borderColor: theme.palette.grayBackground.A20,*/}
        {/*    mx: 'auto',*/}
        {/*    mt: matches({ xs: 3, lg: 4 }),*/}
        {/*    mb: matches({ xs: 6, lg: 6 })*/}
        {/*  }}*/}
        {/*/>*/}
      </Stack>

      {/* Container to hold the two inputs and the central button */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}
      >
        <CryptoInput
          label="دریافت می‌کنم"
          options={cryptoOptionsPayInput}
          value={payValue}
          onInputChange={handlePayValueChange}
          onSelectChange={setPayCrypto}
        />

        <Button
          sx={{
            borderRadius: matches({ xs: 20, lg: 24 }),
            width: matches({ xs: 40, lg: 48 }),
            height: matches({ xs: 40, lg: 48 }),
            backgroundColor: '#F8FAFD',
            border: '4px solid #FFFFFF',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            minWidth: matches({ xs: 40, lg: 48 }),
            minHeight: matches({ xs: 40, lg: 48 })
          }}
        >
          <SvgIcon
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.25 9.75L12 6L15.75 9.75"
              stroke="#16181F"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M8.25 14.25L12 18L15.75 14.25"
              stroke="#16181F"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </SvgIcon>
        </Button>

        <CryptoInput
          label="پرداخت می‌کنم"
          options={cryptoOptionsReceiveInput}
          value={receiveValue}
          onInputChange={handleReceiveValueChange}
          onSelectChange={setReceiveCrypto}
        />
      </Box>

      <Stack alignItems="center" mt={3} justifySelf="end">
        <Button
          color="primary"
          variant="contained"
          sx={{ width: matches({ xs: '90%', lg: 341 }) }}
          onClick={() => window.open('https://p.rabex.ir/auth/login', '_blank')}
        >
          <Typography
            variant={matches({ xs: 'body2', lg: 'body1' })}
            weight="bold"
          >
            معامله
          </Typography>
        </Button>
      </Stack>
    </Stack>
  );
};

export default Calculator;
